// COI dual-write ingest contract (cutover workorder §2.1/§2.2) + New-lane dashboard views.
//
// CROSS-REPO WIRE CONTRACT: the request/response shapes here are mirrored by coi-api's Python
// ingest client (rrid-prophy-coi services/ingest.py). Field names are the workorder §2.1 names
// VERBATIM — snake_case on purpose, unlike the camelCase DTOs in contracts.ts. Do not rename.
import { z } from "zod";
import { CoiIngestSourceSchema, CoiRecordVerdictSchema, type CoiIngestSource, type CoiLane, type CoiRecordVerdict } from "./enums.js";

// ── Ingest request (coi-api → POST /api/admin/coi/ingest) ────────────────────

/** Evidence payload stored as-is in coi_records.evidence (workorder §2.1 keys; extras kept). */
export const CoiIngestEvidenceSchema = z
  .object({
    common_affiliations: z.array(z.string()).nullish(),
    shared_papers: z.array(z.unknown()).nullish(),
    prophy_score: z.number().nullish(),
    prophy_url: z.string().nullish(),
    comment: z.string().nullish(),
  })
  .passthrough();
export type CoiIngestEvidence = z.infer<typeof CoiIngestEvidenceSchema>;

/** Structlog event appended to coi_events — feeds the record timeline (SPEC §5). */
export const CoiIngestEventSchema = z.object({
  event: z.string().min(1),
  detail: z.record(z.unknown()).nullish(),
  /** ISO-8601 with timezone ("Z" or offset — Python's utc isoformat() is fine); defaults to
   *  the server's receive time when omitted. The server normalizes to UTC on write so the
   *  text-column ordering is stable. */
  ts: z.string().datetime({ offset: true }).nullish(),
});
export type CoiIngestEvent = z.infer<typeof CoiIngestEventSchema>;

/** One coi_records upsert. Idempotency key: airtable_record_id when present, else
 *  (preprint_origin_id, reviewer_email). */
export const CoiIngestRecordSchema = z.object({
  airtable_record_id: z.string().min(1).nullish(),
  preprint_origin_id: z.string().min(1),
  manuscript_id: z.string().nullish(),
  reviewer_name: z.string().nullish(),
  reviewer_email: z.string().trim().email(),
  reviewer_orcid: z.string().nullish(),
  verdict: CoiRecordVerdictSchema,
  coi_status: z.string().nullish(),
  status: z.string().nullish(),
  evidence: CoiIngestEvidenceSchema.nullish(),
  // source/shadow/events tolerate explicit null as well as omission — a Python mirror
  // (services/ingest.py) naturally serializes unset fields as null, not absent.
  source: CoiIngestSourceSchema.nullish().transform((v) => v ?? "coi_api"),
  /** true = would-have-written; NOT applied to Airtable (workorder §6 guardrail). */
  shadow: z
    .boolean()
    .nullish()
    .transform((v) => v ?? true),
  events: z
    .array(CoiIngestEventSchema)
    .max(100)
    .nullish()
    .transform((v) => v ?? []),
});
export type CoiIngestRecord = z.infer<typeof CoiIngestRecordSchema>;

/** Body: a single record or a batch. */
export const CoiIngestRequestSchema = z.union([
  CoiIngestRecordSchema,
  z.object({ records: z.array(CoiIngestRecordSchema).min(1).max(500) }),
]);
export type CoiIngestRequest = z.infer<typeof CoiIngestRequestSchema>;

// ── Ingest response (per-record; the route never 500s once authorized) ───────

export interface CoiIngestResultRow {
  index: number;
  /** coi_records.id, or null when the row errored. */
  id: string | null;
  outcome: "inserted" | "updated" | "error";
  error?: string;
}

export interface CoiIngestResponse {
  /** False when any row errored (row detail in results); the batch itself still 200s. */
  ok: boolean;
  results: CoiIngestResultRow[];
  counts: { inserted: number; updated: number; errors: number };
}

// ── New-lane dashboard view (GET /api/admin/coi/new-lane → CoiPanel New lane) ─
// Webapp-internal read model (camelCase like the other views in contracts.ts).

export interface CoiRecordView {
  id: string;
  airtableRecordId: string | null;
  preprintOriginId: string;
  manuscriptId: string | null;
  reviewerName: string | null;
  reviewerEmail: string;
  reviewerOrcid: string | null;
  verdict: CoiRecordVerdict;
  coiStatus: string | null;
  status: string | null;
  evidence: CoiIngestEvidence | null;
  source: CoiIngestSource;
  shadow: boolean;
  createdAt: string;
  updatedAt: string;
  syncedAt: string | null;
}

export interface CoiEventView {
  id: string;
  coiRecordId: string;
  event: string;
  detail: Record<string, unknown> | null;
  ts: string;
}

export interface CoiQueueSampleView {
  id: string;
  lane: CoiLane;
  ts: string;
  fresh: number;
  overdue: number;
  blockedRecent: number;
  blockedStale: number;
  legacy: number;
  total: number;
  verdict: string | null;
}

/** Verdicts that mean "structurally stuck" (New-lane Blocked tab): the engine ran but cannot
 *  proceed without intervention — distinct from `pending` (in-flight) per the honest-state UX. */
export const COI_BLOCKED_VERDICTS: readonly CoiRecordVerdict[] = ["no_match", "auth_error"];

export interface CoiNewLaneView {
  /** False when the read itself failed — render "unavailable", never fake zeros. */
  available: boolean;
  fetchedAt: string;
  totals: {
    records: number;
    /** Rows written in shadow (all of them until COI_INGEST_MODE=live post-cutover). */
    shadow: number;
    byVerdict: Partial<Record<CoiRecordVerdict, number>>;
  };
  /** Latest records (Records tab), each with its coi_events timeline. */
  recent: Array<CoiRecordView & { events: CoiEventView[] }>;
  /** Structurally stuck records (Blocked tab): verdict ∈ COI_BLOCKED_VERDICTS. */
  blocked: CoiRecordView[];
  /** Error surface (Errors tab): verdict=error records + recent error-ish events. */
  errors: { records: CoiRecordView[]; events: CoiEventView[] };
  /** Durable new-lane queue samples (growth over time). */
  samples: CoiQueueSampleView[];
}
